import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';

export interface HolidayOption {
  value: string;
  label: string;
}

@Component({
  selector: 'holiday-popup',
  templateUrl: './holiday-popup.component.html',
  styleUrls: ['./holiday-popup.component.scss']
})
export class HolidayPopupComponent implements OnInit, OnDestroy {
  @Input() openModal!: EventEmitter<boolean>;
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  public isOpen: boolean = false;
  public isPlaying: boolean = true;
  public snowflakes: number[] = [];
  public isStopping: boolean = false;
  public showPlayingText: boolean = false;
  public selectedHoliday: string = 'christmas';

  public holidayOptions: HolidayOption[] = [
    { value: 'christmas', label: 'Christmas' },
    { value: 'hanukkah', label: 'Hanukkah' },
    { value: 'kwanzaa', label: 'Kwanzaa' }
  ];

  private readonly SNOWFLAKE_COUNT = 50;
  private playingTimer: any;
  private stoppingTimer: any;

  constructor(private mqttService: MqttConnectorService) {}

  ngOnInit(): void {
    // Generate snowflake array
    this.snowflakes = Array.from({ length: this.SNOWFLAKE_COUNT }, (_, i) => i);

    this.openModal.subscribe(value => {
      this.isOpen = value;
      if (value) {
        // Reset to playing when opened (but don't send MQTT - only on play button)
        this.isPlaying = true;
        this.isStopping = false;
        this.showPlayingText = false;
        this.clearTimers();
      }
    });
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.playingTimer) {
      clearTimeout(this.playingTimer);
      this.playingTimer = null;
    }
    if (this.stoppingTimer) {
      clearTimeout(this.stoppingTimer);
      this.stoppingTimer = null;
    }
  }

  close(): void {
    this.isOpen = false;
    this.isPlaying = false;
    this.clearTimers();
    // Stop the HA pyscript when closing
    this.mqttService.callHaAction('pyscript', 'holiday_light_sync_stop').subscribe();
    this.onClose.emit();
  }

  play(): void {
    this.isPlaying = true;
    this.isStopping = false;
    this.showPlayingText = true;
    this.clearTimers();

    // Send the selected holiday to Home Assistant
    this.mqttService.callHaAction('pyscript', 'holiday_light_sync_start', {
      holiday: this.selectedHoliday
    }).subscribe();

    // Reset "Playing" text back to "Play" after 90 seconds
    this.playingTimer = setTimeout(() => {
      this.showPlayingText = false;
    }, 90000);
  }

  stop(): void {
    this.isPlaying = false;
    this.isStopping = true;
    this.showPlayingText = false;
    this.clearTimers();

    this.mqttService.callHaAction('pyscript', 'holiday_light_sync_stop').subscribe();

    // Reset "Stopping" text back to "Stop" after 3.5 seconds
    this.stoppingTimer = setTimeout(() => {
      this.isStopping = false;
    }, 3500);
  }

  getSnowflakeStyle(index: number): { [key: string]: string } {
    // Use sine-based pseudo-random for better distribution
    // Each property uses a different seed offset to avoid correlation
    const seed1 = Math.sin(index * 12.9898 + 78.233) * 43758.5453;
    const seed2 = Math.sin(index * 93.9898 + 67.345) * 24531.1234;
    const seed3 = Math.sin(index * 45.1234 + 12.987) * 98765.4321;
    const seed4 = Math.sin(index * 78.5432 + 45.678) * 56789.0123;
    const seed5 = Math.sin(index * 23.4567 + 89.012) * 34567.8901;

    const random1 = seed1 - Math.floor(seed1);
    const random2 = seed2 - Math.floor(seed2);
    const random3 = seed3 - Math.floor(seed3);
    const random4 = seed4 - Math.floor(seed4);
    const random5 = seed5 - Math.floor(seed5);

    // Spread snowflakes evenly across the width
    const left = random1 * 100;

    // Spread animation delays evenly across 0-15s to prevent clumping
    // Use index to ensure even spread, then add randomness
    const baseDelay = (index / this.SNOWFLAKE_COUNT) * 15;
    const delayVariation = (random2 - 0.5) * 3; // +/- 1.5s variation
    const animationDelay = Math.max(0, baseDelay + delayVariation);

    // Duration between 5-15 seconds
    const animationDuration = 5 + random3 * 10;

    // Opacity between 0.4-1.0
    const opacity = 0.4 + random4 * 0.6;

    // Size between 4-12px
    const size = 4 + random5 * 8;

    return {
      'left': `${left}%`,
      'animation-delay': `${animationDelay}s`,
      'animation-duration': `${animationDuration}s`,
      'opacity': `${opacity}`,
      'width': `${size}px`,
      'height': `${size}px`
    };
  }
}
